import { ethers } from 'ethers';
import { WalletProvider, WALLET_TYPES } from './WalletProvider.js';

/**
 * MetaMask wallet provider implementation
 * 
 * Handles connection to MetaMask browser extension using the injected
 * window.ethereum provider. Supports account switching, network validation,
 * and MetaMask-specific event handling.
 */
export class MetaMaskProvider extends WalletProvider {
  constructor() {
    super();
    this.provider = null;
    this.signer = null;
    this.connectionError = '';
    this.eventListeners = new Map();
    
    // Network configuration
    this.SUPPORTED_NETWORKS = {
      31337: { name: 'Local Hardhat', environment: 'development' },
      31338: { name: 'DevCLI', environment: 'development' },
      80002: { name: 'Polygon Amoy', environment: 'test' },
      137: { name: 'Polygon Mainnet', environment: 'preprod_and_production' }
    };
  }

  getType() {
    return WALLET_TYPES.METAMASK;
  }

  getDisplayName() {
    return 'MetaMask';
  }

  isAvailable() {
    return typeof window !== 'undefined' && 
           typeof window.ethereum !== 'undefined' && 
           window.ethereum.isMetaMask;
  }

  async initialize() {
    if (!this.isAvailable()) {
      this.connectionError = 'MetaMask not detected. Please install MetaMask browser extension.';
      throw new Error(this.connectionError);
    }

    this.provider = new ethers.providers.Web3Provider(window.ethereum);
    this.setupEventListeners();
    console.log('🦊 MetaMask provider initialized');
  }

  async connect() {
    if (!this.provider) {
      await this.initialize();
    }

    try {
      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      this.signer = this.provider.getSigner();
      this.connectionError = '';
      
      console.log('✅ Connected to MetaMask');
      
      // Validate network and account
      await this.validateConnection();
      
      return true;
    } catch (error) {
      if (error.code === 4001) {
        this.connectionError = 'User denied account access. Please allow access to your wallet.';
      } else {
        this.connectionError = `Connection failed: ${error.message}`;
      }
      console.error('❌ MetaMask connection failed:', error);
      throw new Error(this.connectionError);
    }
  }

  async disconnect() {
    // MetaMask doesn't have a programmatic disconnect method
    // We just clear our internal state
    this.signer = null;
    this.connectionError = '';
    this.removeEventListeners();
    console.log('🔌 MetaMask provider disconnected');
  }

  getProvider() {
    // Auto-initialize provider for read-only operations if available but not initialized
    if (!this.provider && this.isAvailable()) {
      try {
        this.provider = new ethers.providers.Web3Provider(window.ethereum);
        console.log('🦊 MetaMask provider auto-initialized for read-only operations');
      } catch (error) {
        console.warn('Failed to auto-initialize MetaMask provider:', error);
      }
    }
    return this.provider;
  }

  getSigner() {
    return this.signer;
  }

  async getAccounts() {
    if (!this.provider) {
      return [];
    }

    try {
      const accounts = await this.provider.listAccounts();
      return accounts;
    } catch (error) {
      console.error('Error getting accounts:', error);
      return [];
    }
  }

  async getNetwork() {
    if (!this.provider) {
      return null;
    }

    try {
      const network = await this.provider.getNetwork();
      return {
        chainId: network.chainId,
        name: network.name,
        networkInfo: this.SUPPORTED_NETWORKS[network.chainId]
      };
    } catch (error) {
      console.error('Error getting network:', error);
      return null;
    }
  }

  isConnected() {
    return this.provider !== null && this.signer !== null;
  }

  getErrorMessage() {
    return this.connectionError;
  }

  getEventConfig() {
    return {
      fallbackInterval: 15000, // Reduced frequency to respect API rate limits
      connectionTimeout: 30000,
      supportsRealTimeEvents: true
    };
  }

  // MetaMask-specific validation methods
  async validateNetwork() {
    if (!this.provider) {
      return {
        isValid: false,
        error: 'No wallet provider found. Please install MetaMask.',
        needsConnection: true
      };
    }

    try {
      const network = await this.provider.getNetwork();
      const currentChainId = network.chainId;
      const networkInfo = this.SUPPORTED_NETWORKS[currentChainId];
      
      if (!networkInfo) {
        const supportedChains = Object.entries(this.SUPPORTED_NETWORKS)
          .map(([id, info]) => `${info.name} (${id})`)
          .join(', ');
        
        return {
          isValid: false,
          error: `Unsupported network detected (Chain ID: ${currentChainId}). Please switch to one of: ${supportedChains}`,
          currentChainId,
          supportedNetworks: this.SUPPORTED_NETWORKS
        };
      }
      
      return { 
        isValid: true, 
        network: networkInfo,
        chainId: currentChainId
      };
    } catch (error) {
      return {
        isValid: false,
        error: `Network validation failed: ${error.message}`
      };
    }
  }

  async validateSigner() {
    if (!this.provider) {
      return {
        isValid: false,
        error: 'No Web3 provider available.',
        needsConnection: true
      };
    }

    try {
      const accounts = await this.provider.listAccounts();
      if (accounts.length === 0) {
        return {
          isValid: false,
          error: 'No accounts connected. Please connect your wallet.',
          needsConnection: true
        };
      }

      if (!this.signer) {
        return {
          isValid: false,
          error: 'Wallet connected but signer not available. Please unlock your wallet.',
          needsConnection: true
        };
      }

      // Verify signer can get address
      await this.signer.getAddress();
      
      return { 
        isValid: true,
        account: accounts[0]
      };
    } catch (error) {
      return {
        isValid: false,
        error: `Signer validation failed: ${error.message}`,
        needsConnection: true
      };
    }
  }

  async validateConnection() {
    const networkValidation = await this.validateNetwork();
    const signerValidation = await this.validateSigner();
    
    if (!networkValidation.isValid) {
      throw new Error(networkValidation.error);
    }
    
    if (!signerValidation.isValid) {
      throw new Error(signerValidation.error);
    }
    
    return true;
  }

  // Event handling methods
  onAccountsChanged(callback) {
    this.addEventListener('accountsChanged', callback);
  }

  onChainChanged(callback) {
    this.addEventListener('chainChanged', callback);
  }

  onConnect(callback) {
    this.addEventListener('connect', callback);
  }

  onDisconnect(callback) {
    this.addEventListener('disconnect', callback);
  }

  // Private methods
  setupEventListeners() {
    if (!window.ethereum) return;

    // Remove any existing listeners first
    this.removeEventListeners();

    // Account changes
    const accountsChangedHandler = (accounts) => {
      console.log('🦊 MetaMask accounts changed:', accounts);
      this.emit('accountsChanged', accounts);
      
      // Update signer if accounts changed
      if (accounts.length > 0) {
        this.signer = this.provider.getSigner();
      } else {
        this.signer = null;
      }
    };

    // Chain changes
    const chainChangedHandler = (chainId) => {
      const chainIdNumber = parseInt(chainId, 16);
      console.log('🦊 MetaMask chain changed:', chainIdNumber);
      this.emit('chainChanged', chainIdNumber);
      
      // Trigger page reload for network changes to ensure clean state
      window.location.reload();
    };

    // Connection
    const connectHandler = (connectInfo) => {
      console.log('🦊 MetaMask connected:', connectInfo);
      this.emit('connect', connectInfo);
    };

    // Disconnection
    const disconnectHandler = (error) => {
      console.log('🦊 MetaMask disconnected:', error);
      this.emit('disconnect', error);
      this.signer = null;
    };

    // Add event listeners
    window.ethereum.on('accountsChanged', accountsChangedHandler);
    window.ethereum.on('chainChanged', chainChangedHandler);
    window.ethereum.on('connect', connectHandler);
    window.ethereum.on('disconnect', disconnectHandler);

    // Store references for cleanup
    this.eventListeners.set('accountsChanged', accountsChangedHandler);
    this.eventListeners.set('chainChanged', chainChangedHandler);
    this.eventListeners.set('connect', connectHandler);
    this.eventListeners.set('disconnect', disconnectHandler);
  }

  removeEventListeners() {
    if (!window.ethereum) return;

    // Remove all event listeners
    for (const [event, handler] of this.eventListeners) {
      window.ethereum.removeListener(event, handler);
    }
    this.eventListeners.clear();
  }

  addEventListener(event, callback) {
    if (!this.customEventListeners) {
      this.customEventListeners = new Map();
    }
    
    if (!this.customEventListeners.has(event)) {
      this.customEventListeners.set(event, []);
    }
    
    this.customEventListeners.get(event).push(callback);
  }

  emit(event, data) {
    if (this.customEventListeners && this.customEventListeners.has(event)) {
      this.customEventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in MetaMask event callback for ${event}:`, error);
        }
      });
    }
  }
}

export default MetaMaskProvider;